import { useCallback, useState } from 'react';
import {
  getWorkflowInstance,
  getWorkflowInstanceByReference,
} from '../services/workflowService';

const WORKFLOW_REFERENCE_TYPE_TASK = 'TASK';

const formatDate = (isoDate) => {
  if (!isoDate) return '-';

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '-';

  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}/${date.getFullYear()}`;
};

const resolveTaskTitle = (taskItem) =>
  taskItem?.nombreTarea ||
  taskItem?.titulo ||
  taskItem?.title ||
  taskItem?.task ||
  (taskItem?.id ? `Tarea #${taskItem.id}` : 'Tarea');

const resolveTaskPriority = (taskItem) => {
  const raw =
    taskItem?.priority || taskItem?.prioridad || taskItem?.priorityLabel;
  if (typeof raw === 'string' && raw.trim()) return raw;
  return 'Media';
};

export const useTaskWorkflowFlow = () => {
  const [workflowByTaskId, setWorkflowByTaskId] = useState({});
  const [selectedWorkflowFlow, setSelectedWorkflowFlow] = useState(null);
  const [selectedWorkflowRequest, setSelectedWorkflowRequest] = useState(null);
  const [isWorkflowFlowLoading, setIsWorkflowFlowLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState(null);

  const hydrateTaskWorkflows = useCallback(async (taskItems) => {
    if (!Array.isArray(taskItems) || taskItems.length === 0) {
      setWorkflowByTaskId({});
      return;
    }

    const workflowEntries = await Promise.all(
      taskItems.map(async (taskItem) => {
        if (!taskItem?.id) return null;

        try {
          const workflowInstance = await getWorkflowInstanceByReference(
            WORKFLOW_REFERENCE_TYPE_TASK,
            String(taskItem.id)
          );
          return [taskItem.id, workflowInstance || null];
        } catch (lookupError) {
          console.error(
            `Error cargando workflow para tarea ${taskItem.id}:`,
            lookupError
          );
          return [taskItem.id, null];
        }
      })
    );

    const nextWorkflowMap = {};
    workflowEntries.forEach((entry) => {
      if (!entry) return;

      const [taskId, workflowInstance] = entry;
      if (workflowInstance) {
        nextWorkflowMap[taskId] = workflowInstance;
      }
    });

    setWorkflowByTaskId(nextWorkflowMap);
  }, []);

  const openTaskWorkflowFlow = useCallback(
    async (taskItem) => {
      const taskId = taskItem?.id;
      if (!taskId) return;

      const workflowSummary = workflowByTaskId[taskId];
      if (!workflowSummary?.id) return;

      setIsWorkflowFlowLoading(true);
      setWorkflowError(null);

      try {
        const detail = await getWorkflowInstance(workflowSummary.id);
        setSelectedWorkflowFlow(detail);
        setSelectedWorkflowRequest({
          task: resolveTaskTitle(taskItem),
          workflow:
            detail?.workflowName || workflowSummary?.workflowName || '-',
          requester:
            detail?.initiatedByName ||
            (detail?.initiatedByUserId
              ? `Usuario #${detail.initiatedByUserId}`
              : '-'),
          date: formatDate(detail?.startedAtUtc),
          priority: resolveTaskPriority(taskItem),
        });
      } catch (workflowLoadError) {
        console.error('Error al cargar detalle del workflow:', workflowLoadError);
        setWorkflowError(
          'No fue posible cargar el detalle del workflow para esta tarea.'
        );
        setSelectedWorkflowFlow(null);
        setSelectedWorkflowRequest(null);
      } finally {
        setIsWorkflowFlowLoading(false);
      }
    },
    [workflowByTaskId]
  );

  const closeTaskWorkflowFlow = useCallback(() => {
    setSelectedWorkflowFlow(null);
    setSelectedWorkflowRequest(null);
  }, []);

  return {
    workflowByTaskId,
    workflowError,
    isWorkflowFlowLoading,
    selectedWorkflowFlow,
    selectedWorkflowRequest,
    hydrateTaskWorkflows,
    openTaskWorkflowFlow,
    closeTaskWorkflowFlow,
    clearWorkflowError: () => setWorkflowError(null),
  };
};

export default useTaskWorkflowFlow;
